<template lang="pug">
    #map
</template>

<script>
/*
<div>Icons made by <a href="https://www.freepik.com/" title="Freepik">Freepik</a> from <a href="https://www.flaticon.com/" 			    title="Flaticon">www.flaticon.com</a> is licensed by <a href="http://creativecommons.org/licenses/by/3.0/" 			    title="Creative Commons BY 3.0" target="_blank">CC 3.0 BY</a></div>
*/
import mapboxgl from 'mapbox-gl';
// import U from 'mapbox-gl-utils';
import U from 'map-gl-utils';
import { EventBus } from './EventBus';

import {
    addSchoolLayers,
    highlightSchools,
    switchSchoolSet,
    updateSpecialisationFilter,
    zoomToSchools,
    fitPoints,
} from './schools';

import { addBoundaryLayers, highlightBoundariesForSchools } from './boundaries';

export default {
    mounted() {
        window.app.Map = this;
        initMap.call(this);
        EventBus.$on(
            'change-specialisation-filter',
            updateSpecialisationFilter
        );
        EventBus.$on(
            'schoolSetChanged',
            (schoolSet) => (this.schoolSet = schoolSet)
        );
        EventBus.$on(
            'boundarySetChanged',
            (boundarySet) => (this.boundarySet = boundarySet)
        );
        EventBus.$on(
            'school-select',
            (school, zoomTo, { isZoned, schools } = {}) => {
                this.highlightSchoolAndBoundary(
                    schools && schools.length
                        ? schools
                        : school
                        ? [school]
                        : [],
                    zoomTo
                );
            }
        );
        EventBus.$on('boundaries-results', ({ zonedSchools }) => {
            if (zonedSchools) {
                this.highlightSchoolAndBoundary(zonedSchools, true);
            }
            //
        });
    },
    data: () => ({
        schoolSet: undefined,
        boundarySet: undefined,
        specialisationFilter: undefined,
    }),
    methods: {
        highlightSchoolAndBoundary(schools = [], zoomTo) {
            // if a school was selected directly, make sure it's shown
            const noQuery = schools.every((s) => !s.properties.queryMatch);
            // we don't highlight all zoned schools, see #193
            const relevantSchools = schools.filter(
                (s) =>
                    noQuery ||
                    s.properties.queryMatch === 'exact' ||
                    s.properties.isStandaloneJuniorsec
            );

            const entityCodes = relevantSchools.map(
                (school) => school.properties.Entity_Code
            );
            const filter = relevantSchools.length
                ? ['in', ['get', 'Entity_Code'], ['literal', entityCodes]]
                : false;

            highlightSchools(this.map, {
                filter,
                schools: relevantSchools,
                zoomTo,
                schoolSet: this.schoolSet,
            });
            highlightBoundariesForSchools(this.map, filter, relevantSchools);
            this.$emit('schoolSelected');
        },
        fitPoints(...points) {
            fitPoints(this.map, ...points);
        },
    },
    watch: {
        schoolSet(newSchoolSet, oldSchoolSet) {
            switchSchoolSet(this.map, newSchoolSet, oldSchoolSet);
        },
        specialisationFilter(filter) {
            updateSpecialisationFilter(this.map, filter);
        },
    },
};
import 'mapbox-gl/dist/mapbox-gl.css';

window.app.boundarySetYears = [
    'primary',
    ...window.app.secondaryYears,
    'specialist',
];
window.app.schoolsetYears = [...window.app.boundarySetYears, 'other'];

window.app.hues = {
    primary: 120,
    secondary: 210,
    specialist: 10,
    other: 280,
};

for (let year of window.app.secondaryYears) {
    window.app.hues[year] = window.app.hues.secondary;
}

function preserveDrawingBuffer() {
    // disable this optimisation to allow printing on Firefox
    return (
        navigator.userAgent.match(/firefox/i) &&
        navigator.platform.match(/win|mac|86/i)
    );
}

async function initMap() {
    const devStyle = {
        version: 8,
        sources: {},
        layers: [],
        glyphs: 'http://fonts.openmaptiles.org/{fontstack}/{range}.pbf',
    };
    let prod = window.app.env === 'prod';
    const local = false && window.location.host.match(/localhost/);
    window.app.showBasemap = true;
    const fms = window.location.host.match(/findmyschool/);
    // the prod maptiler key is tied to the prod domain so can't be used for dev
    const maptilerKey = fms ? 'uiDMgst0WB3kPffvsNfB' : 'eAg18CQUk544VXRjrsJx';
    const prodStyle = !fms
        ? `https://api.maptiler.com/maps/basic/style.json?key=${maptilerKey}`
        : `https://api.maptiler.com/maps/112f3720-1add-4283-8a86-c18a121791f1/style.json?key=${maptilerKey}`;
    mapboxgl.accessToken =
        'pk.eyJ1IjoiY3Jvd2RzcG90MSIsImEiOiIwTFItekZ3In0.M_0CqFroH04TUc-K1_CHXQ'; // AA's mapbox account.

    const map = new mapboxgl.Map({
        container: 'map',
        center: prod ? [145, -37.22] : [145, -37.8],
        zoom: prod ? 7 : 12,
        style: !window.app.showBasemap ? devStyle : prodStyle,
        minZoom: 7,
        maxZoom: 19,
        preserveDrawingBuffer: preserveDrawingBuffer(),
        fadeDuration: 100,
    });
    U.init(map);
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }));
    map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();

    map.doubleClickZoom.disable();

    window.map = map;
    this.map = map;

    map.U.onLoad(async () => {
        await addLayers(map);
        this.$emit('mapLoaded');
    });
    await this.$nextTick();
    window.app.AddressSearch.init(map);
}

/*

*/
/*eslint no-undef: "error"*/
async function addLayers(map) {
    map.U.addVector(
        'properties',
        `${window.app.tileHost}/tiles/properties/index.json`
    ).addLine('properties', {
        sourceLayer: 'properties',
        lineColor: 'lightgrey',
    });
    map.U.addVector(
        'addresses',
        `${window.app.tileHost}/tiles/addresses/index.json`
    ).addSymbol('addresses', {
        sourceLayer: 'addresses',
        textField: '{number}',
        textColor: {
            stops: [
                [16, 'hsl(0,0%,70%)'],
                [18, 'hsl(0,0%,50%)'],
            ],
        },
        textSize: {
            stops: [
                [16, 8],
                [18, 12],
            ],
        },
        textFont: !window.app.showBasemap
            ? ['Klokantech Noto Sans Regular']
            : ['DIN Offc Pro Regular'],
        minzoom: 16,
    });

    addBoundaryLayers(map, window.app.tileHost);
    await window.app.schoolsLoading;
    addSchoolLayers(map, window.app.tileHost);

    window.map.once('data', () => {
        window.app.Map.boundarySet = window.app.BoundarySelector.boundarySet;
        window.app.Map.schoolSet = window.app.BoundarySelector.schoolSet;
    });
    map.U.addGeoJSON('geocode-result').addSymbol('geocode-pin', {
        iconImage: 'geocode-pin',
        iconSize: {
            stops: [
                [11, 0.5],
                [13, 0.75],
            ],
        },
        iconAnchor: 'bottom',
    });

    window.app.App.notReady = false;
}
</script>

<style scoped>
#map {
    width: 100%;
    height: 100%;
    position: absolute;
}

@media print {
    #map {
        width: 2000px;
        border: 1px solid black;
    }
}
</style>

<style>
.labelPopup .mapboxgl-popup-content {
    font-family: 'VIC', 'Avenir', Helvetica, Arial, sans-serif;
    max-width: 150px;
    text-align: center;
    padding: 5px;
    pointer-events: none;
}

.non-selectable-school {
    font-size: 10px;
    padding: 0;
    line-height: 1em;
}
</style>
